import { motion, useAnimation, useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import JumpingLetters from "../JumpingLetters";
import TypingLetters from "../TypingLetters";

import { getCDNUrl } from "../Cloudinary";


const imageVariants = {
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            type: "backInOut"
        }
    },
    hidden: {
        opacity: 0,
        y: 50,
        
    },
    
}

function PopupImage({showPopup, closePopup, children}){
    if(!showPopup){ 
        return null
    }
    return (
        <div className="popup-container" >
            <button className="close-symbol" onClick={closePopup}>
                &times;
            </button>
            <span className="popup-image">
                {children}
            </span>
            
        </div>
    );

}

function ImageContainer ({ alt, image400, image800, image1200, caption }) {
    const controls = useAnimation();
    const ref = useRef(null);
    const inView = useInView(ref);
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        if(inView) {
            controls.start("visible");
        }
    }, [controls, inView])
    return (
        <div>
            <motion.div
                ref={ref}
                animate={controls}
                initial="hidden"
                variants={imageVariants}
                className="image-container"
                whileHover={{
                    scale: 1.03,
                }}

            >   
                <div onClick={()=>setShowPopup(true)}>
                    <ResponsiveImage 
                        alt={alt}
                        image400={image400}
                        image800={image800}
                        image1200={image1200}
                    />
                
                </div>
            
            </motion.div>
            
            <PopupImage showPopup={showPopup} closePopup={()=>setShowPopup(false)}>
                    <img
                        src={image1200}
                        alt={alt} 
                        loading="lazy"
                    />
                    <p>
                        {caption}
                    </p>
            </PopupImage>

        </div>
    );
}

function ResponsiveImage({ alt, image400, image800, image1200, sizes = "(max-width: 600px) 100vw, (max-width: 800px) 50vw, 33vw"}) {
    return (
        <img
            src={image800}
            srcSet={`
                ${image400} 400w,
                ${image800} 800w,
                ${image1200} 1200w,
            `
            }
            sizes={sizes}
            alt={alt}
            loading="lazy"
            style={{ maxWidth: "100%", height: "auto"}}
        />
    );
}

export default function Artwork() {
    const [isHover, setIsHover] = useState(false);

    return (
    
    <motion.div
        initial={{
            opacity: 0,
            x: -50

        }}
        animate={{
            opacity: 1,
            x: 0,
        }}
        exit={{
            opacity: 0,
            x: 50
        }}
        transition={{
            duration: 0.3,
            ease: "backInOut"
        }}
    >   
        {/* <div className="title-container">
            <h1>ARTWORK</h1>
        </div> */}

        <span className="page-title-animated"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        style={{ display: "inline-block", cursor: "pointer" }}
                    >
                        <JumpingLetters text="Artwork" isHover={isHover}/>
        </span>
        
        <div className="page-intro-text">
            <TypingLetters text="Browse through my art pieces:"/>
        </div>

        <div className="image-row">
            <div className="image-column">
                <ImageContainer
                    alt="Acrylic painting of self-portrait with hand holding glowing ball of light"
                    image400={getCDNUrl("light-source-1200_mrynzu", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("light-source-1200_mrynzu", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("light-source-1200_mrynzu")}
                    caption={<p><em>Light Source</em>, Acrylic paint</p>}
                />

                <ImageContainer
                    alt="Pencil drawing of three melting candles"
                    image400={getCDNUrl("melting-candles-1200_ik9xls", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("melting-candles-1200_ik9xls", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("melting-candles-1200_ik9xls")}
                    caption={<p><em>Melting Candles</em>, Pencil</p>}
                />        

            </div>
            <div className="image-column">
                <ImageContainer
                    alt="Mixed media still life with a sleeping fox"
                    image400={getCDNUrl("sleeping-fox-1200_zzlsfx", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("sleeping-fox-1200_zzlsfx", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("sleeping-fox-1200_zzlsfx")}
                    caption={<p><em>Nap Time</em>, Mixed medium</p>}
                />

                <ImageContainer
                    alt="Acrylic painting of a jellyfish alien creature crushing a spaceship"
                    image400={getCDNUrl("jellyfish-alien-1200_oifu77", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("jellyfish-alien-1200_oifu77", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("jellyfish-alien-1200_oifu77")}
                    caption={<p><em>Jellyfish Attack</em>, Acrylic paint</p>}
                /> 
            </div>

            <div className="image-column">
                <ImageContainer
                    alt="Digital illustration of a reaper surrounded by dangling clocks"
                    image400={getCDNUrl("times-up-1200_labyx1", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("times-up-1200_labyx1", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("times-up-1200_labyx1")}
                    caption={<p><em>Time's Up</em>, Digital</p>}
                />

                <ImageContainer
                    alt="Mixed media still life with a reflective rainbow skull"
                    image400={getCDNUrl("rainbow-skull-1200_ncpuxg", "w_400,q_auto,f_auto")}
                    image800={getCDNUrl("rainbow-skull-1200_ncpuxg", "w_800,q_auto,f_auto")}
                    image1200={getCDNUrl("rainbow-skull-1200_ncpuxg")}
                    caption={<p><em>Reflect</em>, Mixed medium</p>}
                />
            </div>

        </div>
    

    </motion.div>
    );
}